import type {
  FieldValidationResult,
  FieldValidator,
} from '@payaca/types/fieldValidationTypes';
import type {
  PaymentScheduleExplainer,
  StoredPaymentSchedule,
  StoredPaymentScheduleStage,
} from '@payaca/types/payment-schedule';
import { get } from 'lodash-es';
import { z } from 'zod/v4';
import { getPaymentScheduleInvalidReasons } from './paymentScheduleHelper';

export type EditablePercentageDepositBasis = {
  type: 'percentage';
  percentageDue: number | null;
};

export type EditableFixedDepositBasis = {
  type: 'fixed';
  fixedAmount: number | null;
  percentageCap: number | null;
};

export type EditableStoredPaymentScheduleStage =
  | {
      type: 'deposit';
      description: string;
      basis: EditablePercentageDepositBasis | EditableFixedDepositBasis;
    }
  | { type: 'instalment'; description: string; percentageDue: number | null };

export type EditableStoredPaymentSchedule = {
  stages: EditableStoredPaymentScheduleStage[];
  explainer?: PaymentScheduleExplainer;
};

const descriptionSchema = z.string().min(1, 'Stage description is required');

const percentageDueSchema = z
  .number()
  .positive('Percentage must be more than 0%')
  .nullable()
  .refine((value) => value !== null, 'Percentage is required');

const editableStoredPaymentScheduleStageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('deposit'),
    description: descriptionSchema,
    basis: z.discriminatedUnion('type', [
      z.object({
        type: z.literal('percentage'),
        percentageDue: percentageDueSchema,
      }),
      z.object({
        type: z.literal('fixed'),
        fixedAmount: z
          .number()
          .positive('Fixed deposit amount must be more than 0')
          .nullable()
          .refine(
            (value) => value !== null,
            'Fixed deposit amount is required'
          ),
        percentageCap: z
          .number()
          .gt(0, 'Deposit cap must be more than 0% and at most 100%')
          .max(100, 'Deposit cap must be more than 0% and at most 100%')
          .nullable(),
      }),
    ]),
  }),
  z.object({
    type: z.literal('instalment'),
    description: descriptionSchema,
    percentageDue: percentageDueSchema,
  }),
]) satisfies z.ZodType<EditableStoredPaymentScheduleStage>;

const toStoredStage = (
  stage: EditableStoredPaymentScheduleStage
): StoredPaymentScheduleStage | null => {
  if (!editableStoredPaymentScheduleStageSchema.safeParse(stage).success) {
    return null;
  }

  if (stage.type === 'instalment') {
    const { description, percentageDue } = stage;
    return percentageDue === null
      ? null
      : { type: 'instalment', description, percentageDue };
  }
  const { description, basis } = stage;
  if (basis.type === 'percentage') {
    return basis.percentageDue === null
      ? null
      : {
          type: 'deposit',
          description,
          basis: { type: 'percentage', percentageDue: basis.percentageDue },
        };
  }
  return basis.fixedAmount === null
    ? null
    : {
        type: 'deposit',
        description,
        basis: {
          type: 'fixed',
          fixedAmount: basis.fixedAmount,
          percentageCap: basis.percentageCap,
        },
      };
};

/** `null` while any stage is incomplete or invalid. */
export const fromEditableStoredPaymentSchedule = (
  paymentSchedule: EditableStoredPaymentSchedule
): StoredPaymentSchedule | null => {
  const stages: StoredPaymentScheduleStage[] = [];
  for (const stage of paymentSchedule.stages) {
    const stored = toStoredStage(stage);
    if (!stored) return null;
    stages.push(stored);
  }
  return { explainer: paymentSchedule.explainer, stages };
};

/**
 * An editable payment schedule that is safe to persist. An empty stages array
 * is valid: it represents "no payment schedule". Each stage's issues land on
 * its own fields; the stored schedule's rules land on `stages` once every
 * stage is complete.
 */
export const editableStoredPaymentScheduleSchema = z
  .object({
    stages: z.array(editableStoredPaymentScheduleStageSchema),
    explainer: z.custom<PaymentScheduleExplainer>().optional(),
  })
  .superRefine((paymentSchedule, ctx) => {
    if (!paymentSchedule.stages.length) return;

    const stored = fromEditableStoredPaymentSchedule(paymentSchedule);
    if (!stored) return;

    for (const message of getPaymentScheduleInvalidReasons(stored)) {
      ctx.addIssue({ code: 'custom', message, path: ['stages'] });
    }
  }) satisfies z.ZodType<EditableStoredPaymentSchedule>;

export const validateEditableStoredPaymentSchedule = (
  paymentSchedule: EditableStoredPaymentSchedule | null | undefined
): FieldValidationResult => {
  if (!paymentSchedule) return { isValid: true, errors: [] };

  const result = editableStoredPaymentScheduleSchema.safeParse(paymentSchedule);
  return {
    isValid: result.success,
    errors: result.success
      ? []
      : [...new Set(result.error.issues.map((issue) => issue.message))],
  };
};

export const getEditableStoredPaymentScheduleFieldValidator =
  (): FieldValidator => (fieldName, formState) =>
    validateEditableStoredPaymentSchedule(
      get(formState, fieldName) as EditableStoredPaymentSchedule | null
    );
